<template>
  <div class="row mb-3">
    <div class="col-12">
      <div class="card">
        <div class="card-body py-3">
          <div class="row align-items-center">
            <!-- Main Action Buttons -->
            <div class="col-md-8">
              <div class="d-flex gap-2 flex-wrap align-items-center">
                <!-- Add Internet Point -->
                <button @click="$emit('addPoint')" class="btn btn-primary btn-sm">
                  <i class="mdi mdi-plus-circle me-1"></i> Add Internet Point
                </button>

                <!-- View Toggle -->
                <div class="btn-group">
                  <button @click="$emit('toggleView')" class="btn btn-outline-primary btn-sm">
                    <i :class="`mdi mdi-${getViewIcon()} me-1`"></i>
                    {{ getViewText() }}
                  </button>
                </div>

                <!-- Provider Filter Dropdown -->
                <div class="btn-group">
                  <button type="button" class="btn btn-info btn-sm dropdown-toggle" data-bs-toggle="dropdown">
                    <i class="mdi mdi-domain me-1"></i>
                    Providers
                    <span class="badge bg-light text-dark ms-1">{{ activeProviders.length }}</span>
                  </button>
                  <div class="dropdown-menu" style="min-width: 250px;">
                    <h6 class="dropdown-header">Filter by Provider</h6>
                    <a class="dropdown-item" href="#" @click.prevent="$emit('filterByProvider', 'all')">
                      <i class="mdi mdi-all-inclusive me-1"></i> All Providers
                    </a>
                    <div class="dropdown-divider"></div>
                    <a class="dropdown-item d-flex align-items-center"
                       href="#"
                       v-for="provider in sortedProviders"
                       :key="provider.name"
                       @click.prevent="$emit('filterByProvider', provider.name)">
                      <div class="provider-color-dot me-2" :style="`background-color: ${provider.color}`"></div>
                      <img :src="getProviderLogo(provider.name)" width="20" height="20" class="rounded me-2" :alt="provider.name">
                      <div class="flex-grow-1">
                        <div class="fw-bold">{{ provider.name }}</div>
                        <small class="text-muted">{{ provider.points }} coverage points</small>
                      </div>
                    </a>
                  </div>
                </div>

                <!-- Statistics -->
                <button @click="$emit('showStats')" class="btn btn-success btn-sm">
                  <i class="mdi mdi-chart-bar me-1"></i> Analytics
                </button>

                <!-- Export Options -->
                <div class="btn-group">
                  <button type="button" class="btn btn-warning btn-sm dropdown-toggle" data-bs-toggle="dropdown">
                    <i class="mdi mdi-download me-1"></i> Export
                  </button>
                  <div class="dropdown-menu">
                    <a class="dropdown-item" href="#" @click.prevent="$emit('exportData', 'csv')">
                      <i class="mdi mdi-file-delimited me-1"></i> Export as CSV
                    </a>
                    <a class="dropdown-item" href="#" @click.prevent="$emit('exportData', 'excel')">
                      <i class="mdi mdi-microsoft-excel me-1"></i> Export as Excel
                    </a>
                    <a class="dropdown-item" href="#" @click.prevent="$emit('exportData', 'pdf')">
                      <i class="mdi mdi-file-pdf me-1"></i> Export as PDF
                    </a>
                    <div class="dropdown-divider"></div>
                    <a class="dropdown-item" href="#" @click.prevent="$emit('exportData', 'provider-report')">
                      <i class="mdi mdi-chart-line me-1"></i> Provider Analysis Report
                    </a>
                  </div>
                </div>
              </div>
            </div>

            <!-- Quick Stats -->
            <div class="col-md-4">
              <div class="d-flex justify-content-end gap-3 align-items-center flex-wrap">
                <div class="text-center">
                  <h6 class="mb-0 text-muted small">Total Points</h6>
                  <h5 class="mb-0 text-primary">{{ stats.total || 0 }}</h5>
                </div>
                <div class="vr d-none d-md-block"></div>
                <div class="text-center">
                  <h6 class="mb-0 text-muted small">Providers</h6>
                  <h5 class="mb-0 text-success">{{ stats.providers || 0 }}</h5>
                </div>
                <div class="vr d-none d-md-block"></div>
                <div class="text-center">
                  <h6 class="mb-0 text-muted small">Fiber</h6>
                  <h5 class="mb-0 text-info">{{ stats.fiber || 0 }}</h5>
                </div>
                <div class="vr d-none d-md-block"></div>
                <div class="text-center">
                  <h6 class="mb-0 text-muted small">4G/5G</h6>
                  <h5 class="mb-0 text-warning">{{ stats.fourG || 0 }}</h5>
                </div>
              </div>
            </div>
          </div>

          <!-- Provider Quick Stats Bar -->
          <div class="row mt-3">
            <div class="col-12">
              <div class="provider-stats-bar">
                <div class="d-flex align-items-center">
                  <span class="text-muted me-3 small"><strong>Top Providers:</strong></span>
                  <div class="d-flex gap-3 flex-wrap">
                    <div v-for="provider in topProviders" :key="provider.name"
                         class="provider-stat-item d-flex align-items-center">
                      <div class="provider-indicator me-1" :style="`background-color: ${provider.color}`"></div>
                      <span class="small text-dark">
                        <strong>{{ provider.name }}</strong>
                        <span class="text-muted">({{ provider.points }})</span>
                      </span>
                    </div>
                    <div v-if="activeProviders.length > 5" class="provider-stat-item">
                      <span class="small text-muted">
                        +{{ activeProviders.length - 5 }} more providers
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Technology Distribution -->
          <div class="row mt-2">
            <div class="col-12">
              <div class="technology-distribution">
                <div class="d-flex align-items-center">
                  <span class="text-muted me-3 small"><strong>Technologies:</strong></span>
                  <div class="d-flex gap-2 flex-wrap">
                    <span class="badge bg-primary">
                      <i class="mdi mdi-ethernet me-1"></i>Fiber: {{ stats.fiber || 0 }}
                    </span>
                    <span class="badge bg-success">
                      <i class="mdi mdi-signal-4g me-1"></i>4G: {{ get4GCount() }}
                    </span>
                    <span class="badge bg-info">
                      <i class="mdi mdi-signal-5g me-1"></i>5G: {{ get5GCount() }}
                    </span>
                    <span class="badge bg-warning">
                      <i class="mdi mdi-wifi me-1"></i>Wireless: {{ stats.wireless || 0 }}
                    </span>
                    <span class="badge bg-secondary">
                      <i class="mdi mdi-satellite-variant me-1"></i>Satellite: {{ getSatelliteCount() }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'InternetToolBar',
  props: {
    viewMode: { type: String, default: 'map' },
    stats: { type: Object, default: () => ({}) },
    activeProviders: { type: Array, default: () => [] }
  },
  computed: {
    sortedProviders() {
      return [...this.activeProviders].sort((a, b) => b.points - a.points);
    },
    topProviders() {
      return this.sortedProviders.slice(0, 5);
    }
  },
  methods: {
    getViewIcon() {
      const icons = {
        'map': 'table-large',
        'table': 'chart-bar',
        'providers': 'map'
      };
      return icons[this.viewMode] || 'table-large';
    },

    getViewText() {
      const texts = {
        'map': 'Table View',
        'table': 'Provider Analysis',
        'providers': 'Map View'
      };
      return texts[this.viewMode] || 'Table View';
    },

    getProviderLogo(providerName) {
      const logos = {
        'Safaricom': 'assets/images/providers/safaricom-logo.png',
        'Zuku (Wananchi Group)': 'assets/images/providers/zuku-logo.png',
        'Airtel Kenya': 'assets/images/providers/airtel-logo.png',
        'Telkom Kenya': 'assets/images/providers/telkom-logo.png',
        'JTL Faiba': 'assets/images/providers/faiba-logo.png',
        'Poa Internet': 'assets/images/providers/poa-logo.png',
        'Liquid Telecom': 'assets/images/providers/liquid-logo.png',
        'Mawingu Networks': 'assets/images/providers/mawingu-logo.png'
      };
      return logos[providerName] || 'assets/images/providers/default-provider.png';
    },

    get4GCount() {
      return this.stats.fourG ? Math.floor(this.stats.fourG * 0.7) : 0; // Assume 70% of 4G/5G is 4G
    },

    get5GCount() {
      return this.stats.fourG ? Math.ceil(this.stats.fourG * 0.3) : 0; // Assume 30% of 4G/5G is 5G
    },

    getSatelliteCount() {
      return this.stats.satellite || 0;
    }
  }
}
</script>

<style scoped>
.btn-sm {
  padding: 0.375rem 0.75rem;
  font-size: 0.875rem;
}

.vr {
  width: 1px;
  background-color: #dee2e6;
  height: 40px;
}

.provider-color-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  display: inline-block;
}

.provider-indicator {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;
}

.provider-stats-bar {
  background-color: #f8f9fa;
  border-radius: 6px;
  padding: 8px 12px;
  border: 1px solid #e9ecef;
}

.technology-distribution {
  background-color: #fff;
  border-radius: 6px;
  padding: 6px 12px;
  border: 1px solid #e9ecef;
}

.provider-stat-item {
  padding: 2px 6px;
  background-color: rgba(255, 255, 255, 0.7);
  border-radius: 4px;
  border: 1px solid rgba(0, 0, 0, 0.1);
}

.dropdown-menu {
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
  border: 1px solid rgba(0, 0, 0, 0.15);
}

.dropdown-item {
  transition: all 0.2s ease;
}

.dropdown-item:hover {
  background-color: #f8f9fa;
  transform: translateX(2px);
}

.badge {
  font-size: 0.75rem;
}

.card {
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
  border: 1px solid rgba(0, 0, 0, 0.125);
}

@media (max-width: 768px) {
  .d-flex.gap-2 {
    gap: 0.5rem !important;
  }

  .btn-sm {
    padding: 0.25rem 0.5rem;
    font-size: 0.8rem;
  }

  .vr {
    display: none !important;
  }

  .text-center h5 {
    font-size: 1rem;
  }

  .text-center h6 {
    font-size: 0.75rem;
  }
}
</style>